import { msg } from "@lingui/core/macro";
import { createRoute, useNavigate, useSearch } from "@tanstack/solid-router";
import { createEffect, createMemo, createSignal, For, onMount, Show } from "solid-js";
import { Tags, Trash2, X } from "@fleet/icons";
import {
  computeExpiryAlerts,
  createLotMutation,
  deleteLotMutation,
  getLotsQuery,
  getProductsQuery,
  isTracked,
  queryKeys,
} from "@stockpilot/shared";
import { createPowerSyncQuery } from "@stockpilot/shared/solid";
import { appRoute } from "./route";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { useOrgId } from "@/features/inventory/use-org-id";
import { takeInitialSearchParam } from "@/lib/initial-search";
import { PageContainer, PageHeader } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Select } from "@/components/ui/select";
import { SwipeTableRow } from "@/components/ui/swipe-row";
import type { LotRecord, ProductRecord } from "@stockpilot/powersync";

/** Days within which it is considered "impending expiration". */
const EXPIRY_WINDOW_DAYS = 30;

function LotsPage() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const orgId = useOrgId();
  const search = useSearch({ from: "/_app/lots" });
  const navigate = useNavigate();

  const [productId, setProductId] = createSignal("");
  const [lotName, setLotName] = createSignal("");
  const [expiry, setExpiry] = createSignal("");

  // Router deferred flush guard: if boot lost `?product=` from the
  // initial deep link, reapply it synchronously from the snapshot.
  onMount(() => {
    const initialProduct = takeInitialSearchParam("product", "/lots");
    if (initialProduct && !(search().product ?? "")) {
      void navigate({ to: "/lots", search: { product: initialProduct }, replace: true });
    }
  });

  // Deep-link filter: /lots?product=<id> arrives from stock/products drill-down.
  const productFilter = (): string => search().product ?? "";
  createEffect(() => {
    const pid = productFilter();
    if (pid) {
      setProductId(pid);
    }
  });
  const clearProductFilter = () => {
    void navigate({ to: "/lots", search: {} });
  };

  const products = createPowerSyncQuery<ProductRecord>({
    queryKey: () => queryKeys.inventory.products(orgId()),
    queryFn: () => getProductsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const lots = createPowerSyncQuery<LotRecord>({
    queryKey: () => queryKeys.inventory.lots(orgId()),
    queryFn: () => getLotsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  // Only tracked products (lot/serial) can have lots.
  const trackedProducts = createMemo(() =>
    (products.data ?? []).filter((p) => isTracked(p.tracking)),
  );

  const productName = createMemo(() => {
    const map = new Map((products.data ?? []).map((p) => [p.id, p.name]));
    return (id: string) => map.get(id) ?? id;
  });

  // Visible lots honor the deep-link product filter.
  const visibleLots = createMemo(() => {
    const pid = productFilter();
    const all = lots.data ?? [];
    return pid ? all.filter((l) => l.product_id === pid) : all;
  });

  // Impending expiration alert (CC-6: pure calculation). `today` in ISO date.
  const today = new Date().toISOString().slice(0, 10);
  const expiringAlerts = createMemo(() =>
    computeExpiryAlerts(
      (lots.data ?? []).map((l) => ({
        id: l.id,
        name: l.name,
        product_id: l.product_id,
        expiry_date: l.expiry_date,
      })),
      today,
      EXPIRY_WINDOW_DAYS,
    ),
  );

  async function handleCreate(e: Event) {
    e.preventDefault();
    const d = drizzle();
    const pid = productId();
    const name = lotName().trim();
    if (!d || !pid || !name) {
      return;
    }
    await createLotMutation(d, orgId(), {
      productId: pid,
      name,
      expiryDate: expiry().trim() || null,
    });
    setLotName("");
    setExpiry("");
  }

  async function handleDelete(id: string) {
    const d = drizzle();
    if (!d) {
      return;
    }
    await deleteLotMutation(d, id);
  }

  return (
    <PageContainer>
      <PageHeader
        title={t(msg`Lots & serials`)}
        description={t(msg`Track lot/serial numbers and expiry dates for traceability.`)}
        icon={Tags}
      />

      <form
        class="mt-6 flex flex-wrap items-end gap-2 rounded-md border border-border p-4"
        onSubmit={handleCreate}
        data-testid="lot-form"
      >
        <div class="flex flex-col gap-1">
          <label class="text-xs text-muted-foreground">{t(msg`Tracked product`)}</label>
          <Select
            class="w-48"
            value={productId()}
            onChange={(v) => setProductId(v)}
            data-testid="lot-product-select"
            aria-label={t(msg`Tracked product`)}
            placeholder={t(msg`Select product`)}
            options={trackedProducts().map((p) => ({ value: p.id, label: p.name }))}
            emptyAction={{
              label: t(msg`Add a tracked product`),
              description: t(msg`No lot-tracked products yet`),
              onSelect: () => void navigate({ to: "/products" }),
            }}
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-muted-foreground">{t(msg`Lot / serial number`)}</label>
          <input
            class="w-40 h-10 rounded-md border border-border bg-field-background px-3 text-sm"
            placeholder={t(msg`e.g. LOT-001`)}
            value={lotName()}
            onInput={(e) => setLotName(e.currentTarget.value)}
            data-testid="lot-name-input"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-muted-foreground">{t(msg`Expiry date`)}</label>
          <input
            type="date"
            class="w-40 h-10 rounded-md border border-border bg-field-background px-3 text-sm"
            value={expiry()}
            onInput={(e) => setExpiry(e.currentTarget.value)}
            data-testid="lot-expiry-input"
          />
        </div>
        <Button type="submit" disabled={!drizzle()} data-testid="lot-add-button">
          {t(msg`Add lot`)}
        </Button>
      </form>

      <Show when={trackedProducts().length === 0}>
        <p class="mt-3 text-sm text-muted-foreground" data-testid="lot-no-tracked-hint">
          {t(msg`Mark a product as tracked by lot or serial to register lots.`)}
        </p>
      </Show>

      {/* Impending expiration alert. */}
      <Show when={expiringAlerts().length > 0}>
        <div
          class="mt-6 rounded-md border border-warning/50 bg-warning/10 p-4"
          data-testid="expiry-alert"
        >
          <h2 class="text-sm font-semibold text-warning-foreground">{t(msg`Expiring soon`)}</h2>
          <ul class="mt-2 space-y-1 text-sm">
            <For each={expiringAlerts()}>
              {(a) => (
                <li data-testid="expiry-alert-row" data-lot-id={a.lot.id}>
                  {productName()(a.lot.product_id ?? "")} · {a.lot.name} —{" "}
                  <Show
                    when={a.expired}
                    fallback={
                      <span>
                        {t(msg`Days left`)}: {a.daysToExpiry}
                      </span>
                    }
                  >
                    <span class="font-medium text-danger">{t(msg`Expired`)}</span>
                  </Show>
                </li>
              )}
            </For>
          </ul>
        </div>
      </Show>

      <div class="mt-8 flex flex-wrap items-center gap-3">
        <h2 class="text-lg font-semibold">{t(msg`Lots`)}</h2>
        <Show when={productFilter()}>
          <button
            type="button"
            onClick={clearProductFilter}
            class="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/20"
            data-testid="lots-filter-chip"
          >
            {(() => {
              const filteredName = productName()(productFilter());
              return t(msg`Filtered: ${filteredName}`);
            })()}
            <X class="h-3.5 w-3.5" />
          </button>
        </Show>
      </div>
      <div class="mt-3" data-testid="lot-list">
        <Show
          when={visibleLots().length > 0}
          fallback={
            <EmptyState
              icon={Tags}
              title={t(msg`No lots yet`)}
              description={t(msg`Register a lot or serial number to trace stock.`)}
            />
          }
        >
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-border text-left text-muted-foreground">
                <th class="py-2 font-medium">{t(msg`Product`)}</th>
                <th class="py-2 font-medium">{t(msg`Lot / serial`)}</th>
                <th class="py-2 font-medium">{t(msg`Expiry date`)}</th>
                <th class="py-2" />
              </tr>
            </thead>
            <tbody>
              <For each={visibleLots()}>
                {(l) => (
                  <SwipeTableRow
                    class="border-b border-border/50"
                    data-testid="lot-row"
                    data-lot-id={l.id}
                    data-lot-name={l.name ?? ""}
                    left={{
                      icon: Trash2,
                      label: t(msg`Delete`),
                      tone: "danger",
                      exits: true,
                      onCommit: () => void handleDelete(l.id),
                    }}
                  >
                    <td class="py-2">{productName()(l.product_id ?? "")}</td>
                    <td class="py-2" data-testid="lot-row-name">
                      {l.name}
                    </td>
                    <td class="py-2 tabular-nums">{l.expiry_date ?? "—"}</td>
                    <td class="py-2 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void handleDelete(l.id)}
                        data-testid="lot-delete-button"
                      >
                        {t(msg`Delete`)}
                      </Button>
                    </td>
                  </SwipeTableRow>
                )}
              </For>
            </tbody>
          </table>
        </Show>
      </div>
    </PageContainer>
  );
}

export const lotsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/lots",
  validateSearch: (search: Record<string, unknown>): { product?: string } => ({
    product: typeof search.product === "string" ? search.product : undefined,
  }),
  component: LotsPage,
});
